import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Search,
  TrendingUp,
  History,
  Settings,
  ChevronLeft,
  ChevronRight,
  Star,
  Crown,
  X,
  Sun,
  Moon,
  MessageCircle,
} from 'lucide-react';
import { useSidebarStore, useAuthStore, useThemeStore } from '../../store';

type SubscriptionTier = 'free' | 'starter' | 'early_supporter' | 'pro' | 'enterprise';

// Check if on app subdomain or localhost (for development)
// In production: true for app.stocksentiment.io
// In development: true for localhost (to test app functionality)
const isAppSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('app.') || hostname === 'localhost';
};

// Get base path for app routes
const getAppBasePath = () => isAppSubdomain() ? '' : '/app';

interface NavItem {
  id: string;
  label: string;
  icon: typeof LayoutDashboard;
  pathSuffix: string;  // Path suffix after base
  requiredTier?: 'starter' | 'pro';
}

const navItemsConfig: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, pathSuffix: '' },
  { id: 'scanner', label: 'Market Scanner', icon: Search, pathSuffix: '/scanner' },
  { id: 'analysis', label: 'Stock Analysis', icon: TrendingUp, pathSuffix: '/analysis', requiredTier: 'starter' },
  { id: 'chat', label: 'AI Chat', icon: MessageCircle, pathSuffix: '/chat', requiredTier: 'starter' },
  { id: 'watchlist', label: 'Watchlist', icon: Star, pathSuffix: '/watchlist', requiredTier: 'starter' },
  { id: 'simulator', label: 'Time Simulator', icon: History, pathSuffix: '/simulator', requiredTier: 'pro' },
  { id: 'research', label: 'Research', icon: Search, pathSuffix: '/research', requiredTier: 'pro' },
];

const TIER_HIERARCHY: Record<SubscriptionTier, number> = {
  free: 0,
  starter: 1,
  early_supporter: 1,  // Same level as starter
  pro: 2,
  enterprise: 3,
};

const REQUIRED_TIER_LEVEL: Record<'starter' | 'pro', number> = {
  starter: 1,
  pro: 2,
};

const bottomNavItemsConfig = [
  { id: 'settings', label: 'Settings', icon: Settings, pathSuffix: '/settings' },
];

export function Sidebar() {
  const location = useLocation();
  const { isExpanded, isMobileOpen, toggleSidebar, closeMobile } = useSidebarStore();
  const { user } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();

  const userTier = (user?.subscription_tier || 'free') as SubscriptionTier;
  const userTierLevel = TIER_HIERARCHY[userTier] ?? 0;

  // Build paths based on subdomain
  const basePath = getAppBasePath();
  const navItems = navItemsConfig.map(item => ({
    ...item,
    path: basePath + item.pathSuffix || '/',
  }));
  const bottomNavItems = bottomNavItemsConfig.map(item => ({
    ...item,
    path: basePath + item.pathSuffix,
  }));
  const dashboardPath = basePath || '/';

  const hasAccess = (requiredTier?: 'starter' | 'pro') => {
    if (!requiredTier) return true;
    return userTierLevel >= REQUIRED_TIER_LEVEL[requiredTier];
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden cursor-pointer"
          onClick={closeMobile}
        />
      )}

      <aside
        className={clsx(
          'fixed left-0 top-0 z-50 transition-all duration-300 ease-in-out',
          // Use dvh for mobile browsers to account for browser chrome
          'h-screen md:h-screen',
          // Fallback for older browsers and mobile safari
          'min-h-[100dvh]',
          'bg-gradient-to-b from-[var(--color-sidebar-bg)] to-[var(--color-sidebar-bg)]/95',
          'border-r border-[var(--color-border)] backdrop-blur-sm',
          // Mobile: hidden by default, slide in when open
          'max-md:-translate-x-full max-md:w-64',
          isMobileOpen && 'max-md:translate-x-0',
          // Desktop: normal behavior
          'md:translate-x-0',
          isExpanded ? 'md:w-52' : 'md:w-20'
        )}
      >
      {/* Logo */}
      <div className={clsx(
        'flex h-16 items-center border-b border-[var(--color-border)]',
        'max-md:justify-between max-md:px-4',
        isExpanded ? 'md:justify-between md:px-4' : 'md:justify-center md:px-2'
      )}>
        <Link to={dashboardPath} onClick={closeMobile} className={clsx('flex items-center', 'max-md:gap-3', isExpanded ? 'md:gap-3' : 'md:justify-center')}>
          <img src="/logo.svg" alt="SentimentEdge" className="h-9 w-9" />
          <span className={clsx(
            'text-lg font-bold text-[var(--color-text-primary)] whitespace-nowrap',
            'max-md:block',
            isExpanded ? 'md:block' : 'md:hidden'
          )}>
            SentimentEdge
          </span>
        </Link>
        {/* Mobile close button */}
        <button
          onClick={closeMobile}
          className="md:hidden p-2 rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--sidebar-item-hover)] transition-colors cursor-pointer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex flex-col h-[calc(100dvh-64px)] md:h-[calc(100vh-64px)] justify-between py-4 overflow-y-auto">
        <ul className={clsx('space-y-1', 'max-md:px-3', isExpanded ? 'md:px-3' : 'md:px-2')}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== dashboardPath && item.path !== '/' && location.pathname.startsWith(item.path));
            const canAccess = hasAccess(item.requiredTier);

            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  onClick={closeMobile}
                  className={clsx(
                    'flex items-center rounded-lg py-2.5 transition-all',
                    'text-sm font-medium',
                    'max-md:gap-3 max-md:px-3',
                    isExpanded ? 'md:gap-3 md:px-3' : 'md:justify-center md:px-0',
                    !canAccess && 'opacity-60',
                    isActive
                      ? 'bg-[var(--color-accent-muted)] text-[var(--color-accent)] border border-[var(--accent-green-border)]'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--sidebar-item-hover)] hover:text-[var(--color-text-primary)] border border-transparent'
                  )}
                  title={!isExpanded ? item.label : undefined}
                >
                  <item.icon className={clsx(
                    'h-5 w-5 flex-shrink-0',
                    isActive && 'text-[var(--color-accent)]'
                  )} />
                  <span className={clsx(
                    'flex items-center gap-2 flex-1',
                    'max-md:flex',
                    isExpanded ? 'md:flex' : 'md:hidden'
                  )}>
                    {item.label}
                    {item.requiredTier && !canAccess && (
                      <Crown className={clsx(
                        'h-3.5 w-3.5 ml-auto',
                        item.requiredTier === 'pro' ? 'text-yellow-500' : 'text-emerald-500'
                      )} />
                    )}
                  </span>
                  {!isExpanded && item.requiredTier && !canAccess && (
                    <Crown className={clsx(
                      'h-3 w-3 absolute -top-1 -right-1 max-md:hidden',
                      item.requiredTier === 'pro' ? 'text-yellow-500' : 'text-emerald-500'
                    )} />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className={clsx('space-y-1', 'max-md:px-3', isExpanded ? 'md:px-3' : 'md:px-2')}>
          {bottomNavItems.map((item) => {
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.id}
                to={item.path}
                onClick={closeMobile}
                className={clsx(
                  'flex items-center rounded-lg py-2.5 transition-all',
                  'text-sm font-medium',
                  'max-md:gap-3 max-md:px-3',
                  isExpanded ? 'md:gap-3 md:px-3' : 'md:justify-center md:px-0',
                  isActive
                    ? 'bg-[var(--color-accent-muted)] text-[var(--color-accent)] border border-[var(--accent-green-border)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--sidebar-item-hover)] hover:text-[var(--color-text-primary)] border border-transparent'
                )}
                title={!isExpanded ? item.label : undefined}
              >
                <item.icon className={clsx(
                  'h-5 w-5 flex-shrink-0',
                  isActive && 'text-[var(--color-accent)]'
                )} />
                <span className={clsx('max-md:inline', isExpanded ? 'md:inline' : 'md:hidden')}>
                  {item.label}
                </span>
              </Link>
            );
          })}

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={clsx(
              'flex w-full items-center rounded-lg py-2.5 mt-2 cursor-pointer',
              'text-sm font-medium text-[var(--color-text-muted)] hover:bg-[var(--sidebar-item-hover)] hover:text-[var(--color-text-primary)]',
              'transition-all border border-transparent',
              isExpanded ? 'gap-3 px-3' : 'justify-center px-0',
              'max-md:gap-3 max-md:px-3'
            )}
            title={!isExpanded ? `Switch to ${theme === 'dark' ? 'light' : 'dark'} mode` : undefined}
          >
            {theme === 'dark' ? (
              <Sun className="h-5 w-5 flex-shrink-0" />
            ) : (
              <Moon className="h-5 w-5 flex-shrink-0" />
            )}
            <span className={clsx('max-md:inline', isExpanded ? 'md:inline' : 'md:hidden')}>
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </span>
          </button>

          {/* Collapse Toggle - desktop only */}
          <button
            onClick={toggleSidebar}
            className={clsx(
              'hidden md:flex w-full items-center rounded-lg py-2.5 mt-2 cursor-pointer',
              'text-sm font-medium text-[var(--color-text-muted)] hover:bg-[var(--sidebar-item-hover)] hover:text-[var(--color-text-primary)]',
              'transition-all border border-[var(--color-border)]',
              isExpanded ? 'gap-3 px-3' : 'justify-center px-0'
            )}
          >
            {isExpanded ? (
              <>
                <ChevronLeft className="h-5 w-5" />
                <span>Collapse</span>
              </>
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </button>
        </div>
      </nav>
    </aside>
    </>
  );
}
